import React, { useCallback, useEffect, useMemo } from 'react'
import { Col, Form, Row, Spin, Typography } from 'antd'
import { filter, isEmpty, map, values } from 'lodash'
import PropTypes from 'prop-types'

import {
  DirtyPromptForm,
  FormButtons,
  FormTextareaInput,
  TermErrors,
} from '../../../common/components/sections'
import {
  StyledPageHeader,
  StyledPageTitle,
  StyledVerticalSpace,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import { idPropType } from '../../../common/prop-types'

import { INGREDIENT_COMMON_NEGATIVE_SYNONYM_PHRASE_FIELD_NAMES } from '../../constants'
import { useSaveIngredientCommonNegativeSynonymPhrasesMutation } from '../../hooks'
import { tokenizeTextByNewLine } from '../../utils'

import { ingredientCommonNegativeSynonymPhrasesInitialValues } from './initial-values'
import { validatePhrase } from './validators'

const IngredientCommonNegativeSynonymPhrasesForm = ({
  ingredientCommonNegativeSynonymPhrases,
}) => {
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.INGREDIENT_COMMON_NEGATIVE_SYNONYM_PHRASE.SAVE,
  ])

  const [
    saveIngredientCommonNegativeSynonymPhrasesMutation,
    { isLoading: isSaveLoading, error: saveError },
  ] = useSaveIngredientCommonNegativeSynonymPhrasesMutation()

  const initialData = useMemo(
    () =>
      ingredientCommonNegativeSynonymPhrasesInitialValues(
        ingredientCommonNegativeSynonymPhrases,
      ),
    [ingredientCommonNegativeSynonymPhrases],
  )

  const generateTextareaRules = useCallback(
    textareaName => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          const currentPhrases = tokenizeTextByNewLine(value)

          const otherPhraseFieldName = filter(
            values(INGREDIENT_COMMON_NEGATIVE_SYNONYM_PHRASE_FIELD_NAMES),
            fieldName => fieldName !== textareaName,
          )

          const otherPhrases = map(otherPhraseFieldName, fieldName =>
            tokenizeTextByNewLine(getFieldValue(fieldName)),
          )

          try {
            validatePhrase(currentPhrases, otherPhrases)
            return Promise.resolve()
          } catch (error) {
            return Promise.reject(error)
          }
        },
      }),
    ],
    [],
  )

  const freeFromRules = useMemo(
    () =>
      generateTextareaRules(
        INGREDIENT_COMMON_NEGATIVE_SYNONYM_PHRASE_FIELD_NAMES.FREE_FROM,
      ),
    [generateTextareaRules],
  )
  const physicalPropertysRules = useMemo(
    () =>
      generateTextareaRules(
        INGREDIENT_COMMON_NEGATIVE_SYNONYM_PHRASE_FIELD_NAMES.PHYSICAL_PROPERTY,
      ),
    [generateTextareaRules],
  )
  const locationTypeRules = useMemo(
    () =>
      generateTextareaRules(
        INGREDIENT_COMMON_NEGATIVE_SYNONYM_PHRASE_FIELD_NAMES.LOCATION_TYPE,
      ),
    [generateTextareaRules],
  )
  const otherRules = useMemo(
    () =>
      generateTextareaRules(
        INGREDIENT_COMMON_NEGATIVE_SYNONYM_PHRASE_FIELD_NAMES.OTHER,
      ),
    [generateTextareaRules],
  )

  const handleSaveData = useCallback(
    data => saveIngredientCommonNegativeSynonymPhrasesMutation(data),
    [saveIngredientCommonNegativeSynonymPhrasesMutation],
  )

  useEffect(() => {
    resetForm()
  }, [ingredientCommonNegativeSynonymPhrases, resetForm])

  const { errorDetails } = saveError || {}

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialData}
      name="editIngredientCommonIngredientPhrases"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledPageHeader>
        <StyledPageTitle>Common negative synonyms</StyledPageTitle>
        <FormButtons
          permissions={[
            PERMISSIONS.INGREDIENT_COMMON_NEGATIVE_SYNONYM_PHRASE.SAVE,
          ]}
          isDisabled={!isDirty}
          onReset={resetForm}
        />
      </StyledPageHeader>

      <Typography.Paragraph>
        These negative synonyms expressions will be applied to all
        (ingredients).
      </Typography.Paragraph>

      {!isEmpty(errorDetails) && <TermErrors errors={errorDetails} />}

      <Row gutter={[16]}>
        <Col span={12}>
          <FormTextareaInput
            disabled={!isFormEnabled}
            name="freeFrom"
            label="Free from (enter separated)"
            labelAbove
            placeholder="Input free from phrases..."
            rules={freeFromRules}
            autoSize
          />
        </Col>
        <Col span={12}>
          <StyledVerticalSpace>
            <FormTextareaInput
              disabled={!isFormEnabled}
              name="physicalProperty"
              label="Shape/Color/Size (enter separated)"
              labelAbove
              placeholder="Input physical property phrases..."
              rules={physicalPropertysRules}
            />
            <FormTextareaInput
              disabled={!isFormEnabled}
              name="locationType"
              label="Location Type (enter separated)"
              labelAbove
              placeholder="Input location type phrases..."
              rules={locationTypeRules}
            />
            <FormTextareaInput
              disabled={!isFormEnabled}
              name="other"
              label="Other (enter separated)"
              labelAbove
              placeholder="Input other phrases..."
              rules={otherRules}
            />
          </StyledVerticalSpace>
        </Col>
      </Row>
    </DirtyPromptForm>
  )
}

IngredientCommonNegativeSynonymPhrasesForm.propTypes = {
  ingredientCommonNegativeSynonymPhrases: PropTypes.arrayOf(
    PropTypes.shape({
      id: idPropType.isRequired,
      phrase: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }),
  ),
}

IngredientCommonNegativeSynonymPhrasesForm.defaultProps = {
  ingredientCommonNegativeSynonymPhrases: [],
}

export default IngredientCommonNegativeSynonymPhrasesForm
